import {
  Badge,
  ChoiceChips,
  formatDateTime,
  formatSignedNumber,
  IconButton,
  Select,
  type Column,
} from '@platform/design-system';
import type { TypedClient } from '@platform/openapi';
import { History, Tags } from 'lucide-react';
import type { LabelSelection } from '../labels/labels.ts';
import { KeysetTable } from '../KeysetTable.tsx';
import { pageParams, useKeysetPages } from '../useKeysetPages.ts';
import { queryKeys, usePageContext } from '../usePageContext.ts';
import {
  labelSelectionOf,
  locationNames,
  MOVEMENT_STATES,
  shortId,
  type Location,
  type Movement,
  type MovementState,
} from './inventory.ts';
import { useMovementCounts } from './useMovementCounts.ts';
import { useVariantLabels } from './useVariantLabels.ts';
import styles from './Inventory.module.css';

const ALL = 'all';

const stateTone = { applied: 'success', pending: 'warning', voided: 'neutral' } as const;

export type InventoryMovementsProps = {
  api: TypedClient;
  locations: readonly Location[];
  state: MovementState | null;
  depot: string | null;
  productId: string | null;
  onStateChange: (state: MovementState | null) => void;
  onDepotChange: (depot: string | null) => void;
  onPrintLabels: ((selection: LabelSelection) => void) | null;
};

export const InventoryMovements = (props: InventoryMovementsProps) => {
  const { api, locations, state, depot, productId, onStateChange, onDepotChange, onPrintLabels } = props;
  const { t, locale, tenantId } = usePageContext();
  const counts = useMovementCounts(api, { depot, productId });
  const movements = useKeysetPages<Movement>({
    queryKey: queryKeys.movements(tenantId, { state, depot, productId, paged: true }),
    resource: 'inventory',
    fetchPage: (pageToken) =>
      api.get('/admin/stock-movements', {
        query: {
          ...pageParams(pageToken),
          ...(state === null ? {} : { state }),
          ...(depot === null ? {} : { location_id: depot }),
          ...(productId === null ? {} : { product_id: productId }),
        },
      }),
  });
  const labels = useVariantLabels(
    api,
    movements.items.map((row) => row.variantId),
  );
  const depotNames = locationNames(locations);
  const nameOf = (movement: Movement) =>
    labels.get(movement.variantId)?.productName ?? shortId(movement.variantId);

  const columns: readonly Column<Movement>[] = [
    {
      id: 'occurredAt',
      header: t('inventory.occurredAt'),
      cell: (movement) => (
        <span className={styles['muted']}>{formatDateTime(movement.occurredAt, locale)}</span>
      ),
    },
    {
      id: 'variant',
      header: t('inventory.variantAndDepot'),
      primary: true,
      cell: (movement) => (
        <span className={styles['twoLines']}>
          <strong>{nameOf(movement)}</strong>
          <span>
            {[
              labels.get(movement.variantId)?.sku ?? shortId(movement.variantId),
              depotNames.get(movement.locationId) ?? movement.locationId,
            ].join(' · ')}
          </span>
        </span>
      ),
    },
    {
      id: 'kind',
      header: t('inventory.kind'),
      cell: (movement) => (
        <span className={styles['kind']}>
          <span className={styles['kindTile']} aria-hidden="true">
            <History size={14} />
          </span>
          {movement.reason === null
            ? t(`inventory.kind_${movement.kind}`)
            : t(`adjustStock.reason_${movement.reason}`)}
        </span>
      ),
    },
    {
      id: 'quantity',
      header: t('inventory.quantity'),
      numeric: true,
      cell: (movement) => <strong>{formatSignedNumber(movement.quantity, locale)}</strong>,
    },
    {
      id: 'state',
      header: t('inventory.state'),
      cell: (movement) => (
        <Badge tone={stateTone[movement.state]}>{t(`inventory.state_${movement.state}`)}</Badge>
      ),
    },
    {
      id: 'note',
      header: t('inventory.note'),
      cell: (movement) => <span className={styles['note']}>{movement.note ?? ''}</span>,
    },
  ];

  const labelsColumn = (print: (selection: LabelSelection) => void): Column<Movement> => ({
    id: 'labels',
    header: t('catalog.actions'),
    action: true,
    cell: (movement) => {
      const selection = labelSelectionOf(labels, movement);
      return selection === null ? null : (
        <IconButton
          size="sm"
          label={t('labels.printFor', { name: nameOf(movement) })}
          icon={<Tags size={16} aria-hidden="true" />}
          onClick={() => {
            print(selection);
          }}
        />
      );
    },
  });

  return (
    <KeysetTable
      pages={movements}
      columns={onPrintLabels === null ? columns : [...columns, labelsColumn(onPrintLabels)]}
      rowKey={(movement) => movement.id}
      bare
      toolbar={
        <div className={styles['tabTools']}>
          <ChoiceChips
            legend={t('inventory.state')}
            hideLegend
            locale={locale}
            value={state ?? ALL}
            choices={[
              { value: ALL, label: t('inventory.allMovements'), count: counts.get('all') },
              ...MOVEMENT_STATES.map((candidate) => ({
                value: candidate,
                label: t(`inventory.state_${candidate}`),
                count: counts.get(candidate),
              })),
            ]}
            onChange={(chosen) => {
              onStateChange(MOVEMENT_STATES.find((candidate) => candidate === chosen) ?? null);
            }}
          />
          <Select
            label={t('inventory.depot')}
            hideLabel
            value={depot ?? ALL}
            options={[
              { value: ALL, label: t('inventory.allDepots') },
              ...locations.map((location) => ({ value: location.id, label: location.name })),
            ]}
            onChange={(event) => {
              onDepotChange(event.target.value === ALL ? null : event.target.value);
            }}
          />
        </div>
      }
      shownLabel={(shown, total, count) => t('inventory.movementsShown', { count, shown, total })}
      emptyTitle={t('inventory.noMovements')}
      emptyDescription={t('inventory.noMovementsHelp')}
    />
  );
};
